import { logger } from '@/core/logger';
import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * AsyncStorage wrapper for non-sensitive data storage
 * Provides consistent API with proper error handling and logging
 */
export class AsyncStorageWrapper {
    /**
     * Store a value in AsyncStorage
     * @param key - Storage key
     * @param value - Value to store (will be converted to string)
     * @returns Promise<void>
     */
    static async setItem(key: string, value: string | null): Promise<void> {
        try {
            if (value === null) {
                await AsyncStorage.removeItem(key);
            } else {
                await AsyncStorage.setItem(key, value);
            }
            logger.debug('AsyncStorage:Item stored', { key });
        } catch (error) {
            logger.error('AsyncStorage:Failed to store item', { key, error });
            throw new Error(`Failed to store item: ${key}`);
        }
    }

    /**
     * Retrieve a value from AsyncStorage
     * @param key - Storage key
     * @returns Promise<string | null>
     */
    static async getItem(key: string): Promise<string | null> {
        try {
            const value = await AsyncStorage.getItem(key);
            logger.debug('AsyncStorage:Item retrieved', { key, hasValue: !!value });
            return value;
        } catch (error) {
            logger.error('AsyncStorage:Failed to retrieve item', { key, error });
            return null;
        }
    }

    /**
     * Remove a value from AsyncStorage
     * @param key - Storage key
     * @returns Promise<void>
     */
    static async removeItem(key: string): Promise<void> {
        try {
            await AsyncStorage.removeItem(key);
            logger.debug('AsyncStorage:Item removed', { key });
        } catch (error) {
            logger.error('AsyncStorage:Failed to remove item', { key, error });
            throw new Error(`Failed to remove item: ${key}`);
        }
    }

    /**
     * Store multiple items atomically
     * @param items - Array of [key, value] pairs
     * @returns Promise<void>
     */
    static async setMultiple(items: Array<[string, string | null]>): Promise<void> {
        try {
            // Filter out null values and convert to AsyncStorage format
            const validItems = items
                .filter(([, value]) => value !== null)
                .map(([key, value]) => [key, value!] as [string, string]);

            if (validItems.length > 0) {
                await AsyncStorage.multiSet(validItems);
            }

            // Remove items with null values
            const nullKeys = items
                .filter(([, value]) => value === null)
                .map(([key]) => key);

            if (nullKeys.length > 0) {
                await AsyncStorage.multiRemove(nullKeys);
            }

            logger.debug('AsyncStorage:Multiple items stored', { count: items.length });
        } catch (error) {
            logger.error('AsyncStorage:Failed to store multiple items', { error });
            throw new Error('Failed to store multiple items');
        }
    }

    /**
     * Retrieve multiple items
     * @param keys - Array of keys to retrieve
     * @returns Promise<Record<string, string | null>>
     */
    static async getMultiple(keys: string[]): Promise<Record<string, string | null>> {
        try {
            const values = await AsyncStorage.multiGet(keys);
            const result = Object.fromEntries(values);
            logger.debug('AsyncStorage:Multiple items retrieved', { count: keys.length });
            return result;
        } catch (error) {
            logger.error('AsyncStorage:Failed to retrieve multiple items', { error });
            return {};
        }
    }

    /**
     * Remove multiple items
     * @param keys - Array of keys to remove
     * @returns Promise<void>
     */
    static async removeMultiple(keys: string[]): Promise<void> {
        try {
            await AsyncStorage.multiRemove(keys);
            logger.debug('AsyncStorage:Multiple items removed', { count: keys.length });
        } catch (error) {
            logger.error('AsyncStorage:Failed to remove multiple items', { error });
            throw new Error('Failed to remove multiple items');
        }
    }

    /**
     * Get all keys from AsyncStorage
     * @returns Promise<string[]>
     */
    static async getAllKeys(): Promise<string[]> {
        try {
            const keys = await AsyncStorage.getAllKeys();
            logger.debug('AsyncStorage:All keys retrieved', { count: keys.length });
            return [...keys]; // Convert readonly array to mutable array
        } catch (error) {
            logger.error('AsyncStorage:Failed to get all keys', { error });
            return [];
        }
    }

    /**
     * Clear all data from AsyncStorage
     * @returns Promise<void>
     */
    static async clear(): Promise<void> {
        try {
            await AsyncStorage.clear();
            logger.debug('AsyncStorage:All data cleared');
        } catch (error) {
            logger.error('AsyncStorage:Failed to clear all data', { error });
            throw new Error('Failed to clear all data');
        }
    }

    /**
     * Store an object as JSON
     * @param key - Storage key
     * @param value - Object to store
     * @returns Promise<void>
     */
    static async setObject<T>(key: string, value: T | null): Promise<void> {
        try {
            if (value === null) {
                await this.removeItem(key);
            } else {
                const jsonString = JSON.stringify(value);
                await this.setItem(key, jsonString);
            }
            logger.debug('AsyncStorage:Object stored', { key });
        } catch (error) {
            logger.error('AsyncStorage:Failed to store object', { key, error });
            throw new Error(`Failed to store object: ${key}`);
        }
    }

    /**
     * Retrieve an object from JSON
     * @param key - Storage key
     * @returns Promise<T | null>
     */
    static async getObject<T>(key: string): Promise<T | null> {
        try {
            const jsonString = await this.getItem(key);
            if (!jsonString) return null;

            const parsed = JSON.parse(jsonString) as T;
            logger.debug('AsyncStorage:Object retrieved', { key });
            return parsed;
        } catch (error) {
            logger.error('AsyncStorage:Failed to retrieve object', { key, error });
            return null;
        }
    }
}

// Export default instance for convenience
export const asyncStorage = AsyncStorageWrapper;
